// angular
import { Component, effect, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  FormBuilder,
  FormControlOptions,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { NgClass } from '@angular/common';
// components
import { CheckBoxComponent } from '../checkbox/checkbox.component';
import { StagedInputComponent } from '../staged-input/staged-input.component';
import { RangeInputComponent } from '../range-input/range-input.component';
import { ButtonComponent } from '../button/button.component';
import { RadioButtonComponent } from '../radio-button/radio-button.component';
// services
import { CurrencyService } from '@app/core/shared/services/currency.service';
import { StorageService } from '@app/shared/services/storage.service';
// interfaces & enums
import { PriceType } from '@app/+products/shared/interfaces/product.interface';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
// animations
import { toggleBoxAnimations } from '@app/core/shared/animations/toggle-box.animation';

@Component({
  selector: 'pwj-filters-box',
  standalone: true,
  imports: [
    CheckBoxComponent,
    StagedInputComponent,
    RangeInputComponent,
    ButtonComponent,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    RadioButtonComponent,
    ButtonComponent,
    NgClass,
    TranslateDataPipe,
  ],
  animations: toggleBoxAnimations,
  templateUrl: './filters-box.component.html',
  styleUrl: './filters-box.component.scss',
})
export class FiltersBoxComponent {
  public isBoxOpened: boolean = false;
  public filtersForm!: FormGroup;
  public currentFilters!: any;

  public selectedFilter!: string;
  public currentParams!: Params;
  public queryParams!: Params;

  // inputs
  boxTitle = input.required<string>();
  boxIcon = input.required<string>();
  isClosableBox = input<boolean>(true);
  filtersType = input.required<
    'checkbox' | 'staged' | 'range' | 'radioButton'
  >();
  filterQueryName = input<string>();
  filterItems = input<any[]>();
  isFilterByIdMode = input<boolean>();
  isCategoryFiltering = input<boolean>();
  priceRange = input<{ min: number; max: number; step?: number }>();
  filterPrefix = input<string>();
  filterSuffix = input<string>();
  filterSuffixColor = input<string>();
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;

  // outputs
  onApplyFilters = output<any>();
  onChange = output<any>();

  // dependencies
  private readonly _currenciesService = inject(CurrencyService);
  private readonly _storageService = inject(StorageService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _fb = inject(FormBuilder);

  constructor() {
    // initialize form fields
    this.filtersForm = this._fb.group({});

    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    effect(() => {
      this.currentCurrency = this._currenciesService.currentCurrency();
    });

    effect(() => {
      this.implementFormFields();

      if (this.filterQueryName() && this.filtersType()) {
        // get last applied filters

        this._activatedRoute.queryParams.subscribe(params => {
          if (!params[this.filterQueryName() as string]) {
            // reset box on param changes
            this.filtersForm.reset();
            this.isBoxOpened = false;
          }

          this.currentParams = params;

          if (params[this.filterQueryName() as string]) {
            if (this.filtersType() === 'radioButton') {
              this.currentFilters = params[this.filterQueryName() as string];
            } else {
              const extractFilters = params[
                this.filterQueryName() as string
              ]?.includes(',')
                ? params[this.filterQueryName() as string].split(',')
                : [params[this.filterQueryName() as string]];
              this.currentFilters = extractFilters;
            }

            this.updateFormFields();
          }

          if (this.filtersType() === 'range') {
            this.currentFilters = {
              ...params,
              [this.filterQueryName() as string]: params,
            };
            if (this.currentFilters) {
              this.updateFormFields();
            }
          }
        });
      }
    });
  }

  ngOnChanges(): void {}

  // form functionalities
  public implementFormFields() {
    // Implement filters form controls
    if (this.filterItems() && this.filtersType() !== 'range') {
      this.filterItems()?.map((filter: any) => {
        if (this.isFilterByIdMode() || this.isCategoryFiltering()) {
          // create form field
          this.addFormControl(filter?._id, false);
        } else if (this.filtersType() === 'radioButton') {
          // create form control
          this.addFormControl(this.filterQueryName() as string, '');
        } else {
          // create form control
          this.addFormControl(filter, false);
        }
      });
    }

    if (this.filtersType() == 'range') {
      // create form control
      this.addFormControl(this.filterQueryName() as string, '');
    }
  }

  public updateFormFields() {
    // check if its filter by id mode then use other functionality
    if (this.isFilterByIdMode()) {
      // map on filter items
      this.filterItems()?.map(item => {
        // get form field value from current applied filters
        const filterValue = this.currentFilters?.includes(item?._id);

        // patch value in form
        this.filtersForm.patchValue({
          [item?._id]: filterValue,
        });
      });
    } else if (this.filtersType() === 'radioButton') {
      // update form value
      this.filtersForm.patchValue({
        [this.filterQueryName() as string]: this.currentFilters,
      });
    } else if (this.isCategoryFiltering()) {
      // get form field value
      this.filterItems()?.map(item => {
        if (this.currentFilters?.includes(item._id)) {
          // update form value
          this.filtersForm.patchValue({
            [item?._id]: true,
          });
        } else {
          // update form value
          this.filtersForm.patchValue({
            [item?._id]: false,
          });
        }
      });
    } else if (this.filtersType() === 'range') {
      const lowest_price = this.currentFilters?.lowest_price
        ? JSON.parse(this.currentFilters?.lowest_price)?.[this.currentCurrency]
        : null;

      const highest_price = this.currentFilters?.highest_price
        ? JSON.parse(this.currentFilters?.highest_price)?.[this.currentCurrency]
        : null;

      const currentFilters = {
        ...this.currentFilters,
      };

      if (lowest_price) {
        currentFilters.lowest_price = lowest_price;
      }

      if (highest_price) {
        currentFilters.highest_price = highest_price;
      }

      const priceRange = {
        lowest_price: currentFilters.lowest_price,
        highest_price: currentFilters.highest_price,
      };

      this.filtersForm.patchValue({ ...currentFilters, priceRange });
    } else {
      // get form field value
      this.filterItems()?.map(item => {
        if (this.currentFilters?.includes(item)) {
          // update form value
          this.filtersForm.patchValue({
            [item]: true,
          });
        } else {
          // update form value
          this.filtersForm.patchValue({
            [item]: false,
          });
        }
      });
    }

    // check if filter is selected then open filter box

    this.boxAlreadyOpen();
  }

  public addFormControl(
    fieldName: string,
    value: any = '',
    validators: ValidatorFn | ValidatorFn[] | FormControlOptions | null = []
  ) {
    this.filtersForm.addControl(fieldName, this._fb.control(value, validators));
  }

  // apply filters functionality

  public setFiltersInParams() {
    this.filtersForm.valueChanges.subscribe(fields => {
      if (this.filtersType() === 'range') {
        // pass filters to apply filter function
        this.onApplyFilters.emit(fields[this?.filterQueryName() as string]);
      } else {
        const filters = Object.keys(fields)
          .map(key => {
            if (this.filtersType() === 'radioButton') {
              return fields[key] ? fields[key] : null;
            } else {
              return fields[key] ? key : null;
            }
          })
          .filter(item => item)
          .join(',');

        // pass filters to apply filter function
        this.onApplyFilters.emit(filters);
      }
    });
  }

  // box functionalities
  public setIsBoxOpened() {
    this.isBoxOpened = !this.isBoxOpened;
  }

  public boxAlreadyOpen() {
    if (
      this.filtersType() === 'range' &&
      (this.currentFilters['highest_price'] ||
        this.currentFilters['lowest_price'])
    ) {
      this.isBoxOpened = true;
    } else {
      Object.keys(this.currentParams).forEach(item => {
        if (item && (this.filterQueryName() as string) === item) {
          this.isBoxOpened = true;
        }
      });
    }
  }

  public handleRadioButton(key: string, value: string) {
    let params =
      (this.filterQueryName() as string) === 'category_id'
        ? {
            category_type_id: this.currentParams['category_type_id'],
            [key]: value,
          }
        : { [key]: value };

    this._router.navigate(['.'], {
      relativeTo: this._activatedRoute,
      queryParams: params || {},
      queryParamsHandling: 'merge',
    });
  }

  public setCategoriesFilter(selectedFilter: string) {
    const paramsCategories =
      this.currentParams[this.filterQueryName() as string];

    const extractedCategories =
      paramsCategories &&
      (paramsCategories?.includes(',')
        ? paramsCategories?.split(',')
        : [paramsCategories]);

    let currentCategories = extractedCategories || [];

    if (currentCategories.includes(selectedFilter)) {
      currentCategories = currentCategories.filter(
        (category: string) => category !== selectedFilter
      );
    } else {
      currentCategories.push(selectedFilter);
    }

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {
        [this.filterQueryName() as string]:
          currentCategories?.length > 0 ? currentCategories.join(',') : null,
      },
      queryParamsHandling: 'merge',
    });
  }

  public onDeleteRadioBtnFilter() {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {
        ...this.currentParams,
        [this.filterQueryName() as string]: null,
      },
      queryParamsHandling: 'merge',
    });
  }
}
